import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import type {RagAttachmentDTO} from 'crqlar-react-common/dist/models/ai/ragChat.model';
import type {
    RagThreadDTO,
    RagThreadListRequestDTO,
    RagThreadMessageDTO,
} from 'crqlar-react-common/dist/models/ai/ragThread.model';
import {RagThreadService} from 'crqlar-react-common/dist/services/ai/ragThread.service';
import {resolveCurrentPageName, useAssistantContext} from '../assistantContext';
import {
    AssistantFeedbackRating,
    AssistantMessage,
    createAssistantMessageId,
    mapThreadMessage,
} from '../assistantMessages';

const POLL_INTERVAL_MS = 5_000;
const ACTIVE_THREAD_STORAGE_KEY = 'assistant.activeThreadId';

interface SubmitOptions {
    attachments?: RagAttachmentDTO[];
    humanHandoff?: boolean;
}

interface UseAssistantChatOptions {
    pollingEnabled?: boolean;
}

/**
 * Owns the chat-thread state: the message list, the active RAG thread, and
 * the in-flight "asking" flag. Talks to the thread API (/v3/ai/threads).
 *
 *   submit(text)               — post a question into the current thread
 *                                 (creating one on the first message)
 *   loadThread(id)             — fetch a past thread and replace the list
 *   startNew()                 — drop the current thread and clear messages
 *   regenerateAnswer(id)       — re-run the AI for the latest answer (in place)
 *   editMessage(id, text)      — edit the latest user message and re-answer
 *   requestHandoff()           — hand the current question to the support team
 *   endHandoff()               — take the conversation back from the team
 *
 * Who answers is the server's call, read off `thread.handoffStatus`: NONE means the
 * assistant replies inline, anything else means the team owns the conversation, sends
 * come back with no assistant message, and their replies arrive through the poll.
 */
export const useAssistantChat = ({pollingEnabled = true}: UseAssistantChatOptions = {}) => {
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [threadId, setThreadId] = useState<string | null>(null);
    const [thread, setThread] = useState<RagThreadDTO | null>(null);
    const [isAsking, setIsAsking] = useState(false);
    const [isBootstrapping, setIsBootstrapping] = useState(true);
    const [isLoadingThread, setIsLoadingThread] = useState(false);
    // id of the freshly-generated answer to reveal with a typing animation
    const [streamingMessageId, setStreamingMessageId] = useState<string | null>(null);
    const sessionGenerationRef = useRef(0);
    // bumped to invalidate an in-flight ask's result when the user stops waiting
    const askRef = useRef(0);
    const messagesRef = useRef<AssistantMessage[]>([]);
    const threadIdRef = useRef<string | null>(null);
    const pollRequestRef = useRef(0);

    const assistantContext = useAssistantContext();
    const {t, i18n} = useTranslation();

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const rememberThreadId = useCallback((id: string | null) => {
        threadIdRef.current = id;
        setThreadId(id);
        try {
            if (id) {
                localStorage.setItem(ACTIVE_THREAD_STORAGE_KEY, id);
            } else {
                localStorage.removeItem(ACTIVE_THREAD_STORAGE_KEY);
            }
        } catch {
            // localStorage persistence is an optimization only.
        }
    }, []);

    const replaceWithServerMessages = useCallback((serverMessages: RagThreadMessageDTO[]) => {
        setMessages(serverMessages.map(mapThreadMessage));
    }, []);

    const markThreadReadFor = useCallback(async (
        targetThreadId: string | null,
        seq?: number,
    ) => {
        if (!targetThreadId) return;
        try {
            const savedThread = await RagThreadService.markThreadRead(targetThreadId, {seq});
            setThread(curr =>
                curr?.id === targetThreadId ? savedThread : curr,
            );
        } catch {
            // Read state is advisory; failed updates should not interrupt chat.
        }
    }, []);

    const markRead = useCallback(async (seq?: number) => {
        await markThreadReadFor(threadId, seq);
    }, [threadId, markThreadReadFor]);

    // Stop waiting on the in-flight answer. Gemini still finishes server-side, but for a fresh question
    // (an optimistic local-only user message is showing) we drop that turn locally and ask the server to
    // hide the persisted question + answer everywhere. Regenerate/edit have no optimistic turn, so they
    // just detach (the existing answer stays). Bumping askRef discards the in-flight request's result.
    const cancelAsk = useCallback(() => {
        askRef.current += 1;
        setIsAsking(false);

        const pendingSend = messagesRef.current.some(m => m.isLocalOnly && m.role === 'user');
        if (!pendingSend) return;

        setMessages(curr => curr.filter(m => !(m.isLocalOnly && m.role === 'user')));
        if (threadId) {
            // best-effort: if the flag doesn't land, the next poll just re-shows the turn
            void RagThreadService.cancelAsk(threadId).catch(() => undefined);
        }
    }, [threadId]);

    /**
     * Team replies (and the handoff status flips that go with them) only ever arrive here, so this
     * keeps running for as long as the panel is open — WAITING and ACTIVE conversations included,
     * where it is the sole delivery path.
     *
     * A tick that brings nothing must change nothing on screen. The payload is a full snapshot and
     * its `has*` flags aren't a reliable "something changed" signal (`hasAnsweredMessages` stays
     * true for any thread that has ever been answered), so both pieces of state are diffed against
     * what is rendered: adopting a fresh transcript every 5s would re-render every message, re-parse
     * their markdown, and re-anchor the scroll — a periodic twitch while the customer sits waiting.
     */
    const pollThread = useCallback(async () => {
        if (!threadId || isAsking || isBootstrapping || isLoadingThread) return;

        const targetThreadId = threadId;
        const sessionGeneration = sessionGenerationRef.current;
        const pollRequest = ++pollRequestRef.current;
        const afterSeq = latestSeqOf(messagesRef.current);

        try {
            const response = await RagThreadService.pollThread(targetThreadId, afterSeq);
            if (
                sessionGeneration !== sessionGenerationRef.current
                || targetThreadId !== threadIdRef.current
                || pollRequest !== pollRequestRef.current
            ) return;
            setThread(curr => (curr && sameThreadState(curr, response.thread) ? curr : response.thread));

            const incoming = (response.messages || []).map(mapThreadMessage);
            if (transcriptSignature(incoming) !== transcriptSignature(messagesRef.current)) {
                setMessages(incoming);
            }
        } catch {
            // Polling is background refresh. The next interval can recover.
        }
    }, [threadId, isAsking, isBootstrapping, isLoadingThread]);

    useEffect(() => {
        let cancelled = false;
        const sessionGeneration = sessionGenerationRef.current;

        const loadThreadSnapshot = async (id: string) => {
            const response = await RagThreadService.getThread(id);
            if (cancelled || sessionGeneration !== sessionGenerationRef.current) return true;
            rememberThreadId(response.thread.id || id);
            setThread(response.thread);
            replaceWithServerMessages(response.messages || []);
            return true;
        };

        const bootstrap = async () => {
            setIsBootstrapping(true);
            try {
                const storedThreadId = (() => {
                    try {
                        return localStorage.getItem(ACTIVE_THREAD_STORAGE_KEY);
                    } catch {
                        return null;
                    }
                })();

                if (storedThreadId) {
                    try {
                        const loaded = await loadThreadSnapshot(storedThreadId);
                        if (loaded) return;
                    } catch {
                        rememberThreadId(null);
                    }
                }

                const request: RagThreadListRequestDTO = {limit: {limit: 1, offset: 0}};
                const page = await RagThreadService.listThreads(request);
                const latestThread = page.content?.[0];
                if (latestThread?.id) {
                    await loadThreadSnapshot(latestThread.id);
                    return;
                }

                if (!cancelled && sessionGeneration === sessionGenerationRef.current) {
                    rememberThreadId(null);
                    setThread(null);
                    setMessages([]);
                }
            } catch {
                if (!cancelled && sessionGeneration === sessionGenerationRef.current) {
                    rememberThreadId(null);
                    setThread(null);
                    setMessages([]);
                }
            } finally {
                if (!cancelled && sessionGeneration === sessionGenerationRef.current) {
                    setIsBootstrapping(false);
                }
            }
        };

        void bootstrap();

        return () => {
            cancelled = true;
        };
    }, [rememberThreadId, replaceWithServerMessages]);

    useEffect(() => {
        if (!threadId || !pollingEnabled) return undefined;
        void pollThread();
        const timer = window.setInterval(() => {
            void pollThread();
        }, POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [threadId, pollingEnabled, pollThread]);

    const submit = async (text: string, options: SubmitOptions = {}) => {
        const trimmed = text.trim();
        if (!trimmed || isAsking || isBootstrapping || isLoadingThread) return;

        const sessionGeneration = sessionGenerationRef.current;
        const askId = ++askRef.current;
        setIsAsking(true);

        // Optimistic render — show the user's message instantly. We replace it
        // with the server-canonical message once the round-trip completes.
        const optimisticUserId = createAssistantMessageId();
        setMessages(curr => [
            ...curr,
            {
                id: optimisticUserId,
                role: 'user',
                content: trimmed,
                attachments: options.attachments,
                isLocalOnly: true,
            },
        ]);

        try {
            // First message in a thread: create it, then post into it.
            let activeId = threadId;
            if (!activeId) {
                const created = await RagThreadService.createThread({
                    language: i18n.language,
                });
                if (sessionGeneration !== sessionGenerationRef.current) return;
                activeId = created.id!;
                rememberThreadId(activeId);
                setThread(created);
            }

            const response = await RagThreadService.sendThreadMessage(activeId, {
                text: trimmed,
                language: i18n.language,
                currentPath: assistantContext.currentPath,
                currentPageName: resolveCurrentPageName(),
                metadata: assistantContext,
                attachments: options.attachments,
                humanHandoff: options.humanHandoff,
            });

            // session changed (new chat) or the user stopped waiting → drop this result
            if (sessionGeneration !== sessionGenerationRef.current || askId !== askRef.current) return;
            setThread(response.thread);
            rememberThreadId(response.thread.id || activeId);
            // No assistant message means the team owns the conversation: the question went to them,
            // the banner says so, and their reply lands through the poll whenever they write it.
            const assistantMessage = response.assistantMessage;
            setMessages(curr => [
                ...curr.filter(m => m.id !== optimisticUserId),
                mapThreadMessage(response.userMessage),
                ...(assistantMessage ? [mapThreadMessage(assistantMessage)] : []),
            ]);
            setStreamingMessageId(
                assistantMessage?.status === 'ANSWERED'
                    ? assistantMessage.id ?? null
                    : null,
            );
            void markThreadReadFor(
                activeId,
                assistantMessage?.seq ?? response.userMessage.seq,
            );
        } catch {
            // a stopped or superseded request shouldn't surface an error bubble
            if (sessionGeneration !== sessionGenerationRef.current || askId !== askRef.current) return;
            setMessages(curr => [
                ...curr,
                {
                    id: createAssistantMessageId(),
                    role: 'assistant',
                    content: t('I could not answer that question. Please try again.'),
                    isError: true,
                    isLocalOnly: true,
                },
            ]);
        } finally {
            if (sessionGeneration === sessionGenerationRef.current && askId === askRef.current) {
                setIsAsking(false);
            }
        }
    };

    const loadThread = async (id: string): Promise<boolean> => {
        // A conversation switch is a new chat session. It invalidates an older poll,
        // load, or ask before any of those responses can replace this transcript.
        const sessionGeneration = ++sessionGenerationRef.current;
        askRef.current += 1;
        setIsAsking(false);
        setIsLoadingThread(true);
        setStreamingMessageId(null);
        try {
            const resp = await RagThreadService.getThread(id);
            if (sessionGeneration !== sessionGenerationRef.current) return false;
            rememberThreadId(id);
            setThread(resp.thread);
            replaceWithServerMessages(resp.messages || []);
            const latest = (resp.messages || []).reduce<number | undefined>((acc, m) => {
                if (m.seq === undefined) return acc;
                return acc === undefined ? m.seq : Math.max(acc, m.seq);
            }, undefined);
            void markThreadReadFor(id, latest);
            return true;
        } catch {
            if (sessionGeneration !== sessionGenerationRef.current) return false;
            setMessages([
                {
                    id: createAssistantMessageId(),
                    role: 'assistant',
                    content: t('Could not load that conversation. Please try again.'),
                    isError: true,
                    isLocalOnly: true,
                },
            ]);
            return false;
        } finally {
            if (sessionGeneration === sessionGenerationRef.current) {
                setIsLoadingThread(false);
            }
        }
    };

    const startNew = () => {
        sessionGenerationRef.current += 1;
        setMessages([]);
        setThread(null);
        rememberThreadId(null);
        setIsAsking(false);
        setIsBootstrapping(false);
        setIsLoadingThread(false);
        setStreamingMessageId(null);
    };

    /**
     * Hand the conversation to the support team from under an answer the assistant couldn't give.
     * No new message — the team gets the question that's already there — so the only visible change
     * is the thread's handoff status, which the banner renders.
     */
    const requestHandoff = async () => {
        if (!threadId) return;
        const sessionGeneration = sessionGenerationRef.current;
        try {
            const updated = await RagThreadService.requestHandoff(threadId);
            if (sessionGeneration !== sessionGenerationRef.current) return;
            setThread(updated);
        } catch {
            // nothing was said on the customer's behalf; they can ask again or write to the team
        }
    };

    /**
     * Take the conversation back from the team and carry on with the assistant — same thread, same
     * history (the answer path feeds the team's messages to Gemini too, so follow-ups about what
     * they said work). Valid from every handoff state: after a helpful reply, or as the way out of a
     * request still waiting, or one whose Slack post never landed.
     */
    const endHandoff = async () => {
        if (!threadId) return;
        const sessionGeneration = sessionGenerationRef.current;
        try {
            const updated = await RagThreadService.endHandoff(threadId);
            if (sessionGeneration !== sessionGenerationRef.current) return;
            setThread(updated);
        } catch {
            // the conversation stays with the team; the banner keeps offering the way back
        }
    };

    /**
     * Record a thumb rating (and optional explanation) against an assistant
     * message — or clear it by passing `rating: null`. Optimistic; the API
     * call settles in the background.
     */
    const submitFeedback = async (
        messageId: string,
        rating: AssistantFeedbackRating | null,
        explanation?: string,
    ) => {
        setMessages(curr =>
            curr.map(m => {
                if (m.id !== messageId) return m;
                if (rating === null) {
                    const {feedback, ...rest} = m;
                    return rest;
                }
                return {...m, feedback: {rating, explanation}};
            }),
        );

        try {
            await RagThreadService.giveThreadMessageFeedback({
                messageId,
                rating,
                explanation,
            });
        } catch {
            // leave the optimistic state in place; a retry works the same way
        }
    };

    /**
     * Re-run the AI for the latest assistant answer. The server regenerates it
     * in place (same id/seq). For a local-only error bubble (a send that never
     * reached the server) we drop it and resubmit the user's prompt instead.
     */
    const regenerateAnswer = async (assistantMessageId: string) => {
        if (isAsking || !threadId) return;

        const list = messagesRef.current;
        const idx = list.findIndex(m => m.id === assistantMessageId);
        if (idx < 0) return;
        const assistant = list[idx];
        if (assistant.role !== 'assistant') return;

        if (assistant.isLocalOnly) {
            const user = list[idx - 1];
            const toRemove = new Set<string>([assistantMessageId]);
            if (user?.isLocalOnly) toRemove.add(user.id);
            setMessages(curr => curr.filter(m => !toRemove.has(m.id)));
            if (user && user.role === 'user') {
                await submit(user.content, {
                    attachments: user.attachments as RagAttachmentDTO[] | undefined,
                });
            }
            return;
        }

        const sessionGeneration = sessionGenerationRef.current;
        const askId = ++askRef.current;
        setIsAsking(true);
        try {
            const regenerated = await RagThreadService.regenerateAnswer(threadId, assistantMessageId);
            if (sessionGeneration !== sessionGenerationRef.current || askId !== askRef.current) return;
            const mapped = mapThreadMessage(regenerated);
            setMessages(curr => curr.map(m => (m.id === assistantMessageId ? mapped : m)));
            setStreamingMessageId(regenerated.status === 'ANSWERED' ? (regenerated.id ?? null) : null);
        } catch {
            // leave the existing answer in place; the user can retry
        } finally {
            if (sessionGeneration === sessionGenerationRef.current && askId === askRef.current) {
                setIsAsking(false);
            }
        }
    };

    /**
     * Edit the user's latest message and re-answer. The server updates the
     * user message in place and regenerates its answer; we rebuild the tail
     * from the response (edit is last-turn-only, so nothing trails it).
     */
    const editMessage = async (userMessageId: string, newText: string) => {
        const trimmed = newText.trim();
        if (!trimmed || isAsking || !threadId) return;

        const target = messagesRef.current.find(m => m.id === userMessageId);
        if (!target || target.role !== 'user' || target.isLocalOnly) return;

        const sessionGeneration = sessionGenerationRef.current;
        const askId = ++askRef.current;
        setIsAsking(true);
        // optimistic: show the edited text immediately
        setMessages(curr => curr.map(m => (m.id === userMessageId ? {...m, content: trimmed} : m)));
        try {
            const response = await RagThreadService.editMessage(threadId, userMessageId, {text: trimmed});
            if (sessionGeneration !== sessionGenerationRef.current || askId !== askRef.current) return;
            setThread(response.thread);
            const editedSeq = response.userMessage.seq;
            const assistantMessage = response.assistantMessage;
            setMessages(curr => [
                ...curr.filter(m => m.seq !== undefined && editedSeq !== undefined && m.seq < editedSeq),
                mapThreadMessage(response.userMessage),
                ...(assistantMessage ? [mapThreadMessage(assistantMessage)] : []),
            ]);
            setStreamingMessageId(
                assistantMessage?.status === 'ANSWERED'
                    ? assistantMessage.id ?? null
                    : null,
            );
        } catch {
            // leave the optimistic edit in place; a retry re-runs the same way
        } finally {
            if (sessionGeneration === sessionGenerationRef.current && askId === askRef.current) {
                setIsAsking(false);
            }
        }
    };

    /**
     * Remove a message (and its paired turn) from the thread. Optimistic —
     * the message disappears immediately and is restored if the call fails.
     */
    const deleteMessage = async (messageId: string) => {
        const sessionGeneration = sessionGenerationRef.current;
        const prev = messages;
        const messageToDelete = messages.find(m => m.id === messageId);
        const idsToRemove = getMessagePairIds(messages, messageId);
        setMessages(curr => curr.filter(m => !idsToRemove.includes(m.id)));

        if (messageToDelete?.isLocalOnly || !threadId) {
            return;
        }

        // Send every paired id; sending only the clicked one leaves the partner,
        // which the next poll pulls back (so the delete appears to "do nothing").
        const serverIds = idsToRemove.filter(id => {
            const msg = messages.find(x => x.id === id);
            return msg && !msg.isLocalOnly;
        });
        if (serverIds.length === 0) return;

        try {
            await RagThreadService.deleteThreadMessages({id: serverIds});
        } catch {
            if (sessionGeneration === sessionGenerationRef.current) {
                setMessages(prev);
            }
        }
    };

    return {
        messages,
        threadId,
        thread,
        isAsking,
        streamingMessageId,
        isLoading: isBootstrapping || isLoadingThread,
        submit,
        loadThread,
        startNew,
        submitFeedback,
        deleteMessage,
        regenerateAnswer,
        editMessage,
        requestHandoff,
        endHandoff,
        cancelAsk,
        markRead,
    };
};

// Everything the transcript actually renders per message, cheap to compare. Feedback is left out on
// purpose: a thumb is applied optimistically and the poll must not undo it.
const transcriptSignature = (messages: AssistantMessage[]): string =>
    messages
        .map(m => `${m.id}:${m.seq ?? ''}:${m.status ?? ''}:${m.updateDate ?? ''}:${m.content.length}`)
        .join('|');

// The thread fields the widget renders (banner, unread pill, conversation name).
const sameThreadState = (a: RagThreadDTO, b: RagThreadDTO): boolean =>
    a.id === b.id
    && a.name === b.name
    && a.handoffStatus === b.handoffStatus
    && a.lastMessageSeq === b.lastMessageSeq
    && a.lastMessageAuthor === b.lastMessageAuthor
    && a.userLastReadSeq === b.userLastReadSeq;

const latestSeqOf = (list: AssistantMessage[]): number | undefined =>
    list.reduce<number | undefined>((latest, message) => {
        if (message.seq === undefined) return latest;
        return latest === undefined ? message.seq : Math.max(latest, message.seq);
    }, undefined);

/**
 * An AI question and its answer are one turn: deleting half of it leaves a dangling question or an
 * answer to nothing, so both go. Messages the team is part of are not a turn — each one is somebody
 * speaking — so a team reply, or a question that went to them, is removed on its own.
 */
const getMessagePairIds = (messages: AssistantMessage[], messageId: string): string[] => {
    const index = messages.findIndex(m => m.id === messageId);
    if (index < 0) return [messageId];

    const message = messages[index];
    const paired = message.role === 'user'
        ? messages[index + 1]
        : messages[index - 1];

    if (!paired || paired.role === message.role) {
        return [messageId];
    }

    const answer = message.role === 'assistant' ? message : paired;
    if (answer.origin === 'ADMIN' || answer.origin === 'SLACK') {
        return [messageId];
    }

    return [messageId, paired.id];
};
